import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import type {SettlementPaymentMethod} from '@afriex/core/domain/types';

import {Badge, Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {
  isSettlementPaymentMethodArchived,
  readSettlementAccountField,
} from '@/types';
import * as formatDate from '@/utils/date';

import SettlementAccountSummary from './SettlementAccountSummary';
import {
  settlementChannelFields,
  settlementChannelLabel,
} from './settlementChannelForms';

const SettlementAccountsTable = ({
  items,
  onSaved,
}: {
  items: SettlementPaymentMethod[];
  onSaved?: () => void | Promise<void>;
}) => (
  <Table
    containerDataCy="merchant-settlement-accounts-table"
    className="overflow-hidden"
  >
    <Thead>
      <Tr>
        <Th>&nbsp;</Th>
        <Th>Account</Th>
        <Th>Channel</Th>
        <Th>Currency</Th>
        <Th>Status</Th>
        <Th>Last updated</Th>
      </Tr>
    </Thead>
    <TBody>
      {items.map((item, index) => (
        <SettlementAccountRow
          key={item.id}
          item={item}
          index={index}
          onSaved={onSaved}
        />
      ))}
    </TBody>
  </Table>
);

const SettlementAccountRow = ({
  item,
  index,
  onSaved,
}: {
  item: SettlementPaymentMethod;
  index: number;
  onSaved?: () => void | Promise<void>;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  // The first two fields of whichever channel this is — the account name and
  // then its identifier (account number, phone, email, UPI id) — so a mobile
  // money row does not have to pretend to have a bank. Read from the registry,
  // which means a new channel needs no change here.
  const [primary, secondary] = settlementChannelFields(item.channel)
    .slice(0, 2)
    .map((field) => readSettlementAccountField(item.info, field.name));

  return (
    <>
      <Tr className={zebraRow} data-cy="merchant-settlement-accounts-row">
        <Td isNumeric>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
            dataCy="merchant-settlement-accounts-row-expand"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>
        <Td>
          <div className="flex flex-col">
            <span
              className="font-medium"
              data-cy="merchant-settlement-accounts-row-account-name"
            >
              {primary}
            </span>
            <span
              className="text-xs text-gray-500 dark:text-gray-400"
              data-cy="merchant-settlement-accounts-row-account-identifier"
            >
              {secondary}
            </span>
          </div>
        </Td>
        <Td data-cy="merchant-settlement-accounts-row-channel">
          {settlementChannelLabel(item.channel)}
        </Td>
        <Td data-cy="merchant-settlement-accounts-row-currency">
          {item.currencyCode}
        </Td>
        <Td data-cy="merchant-settlement-accounts-row-status">
          <Badge
            label={
              isSettlementPaymentMethodArchived(item) ? 'Archived' : 'Active'
            }
            colorScheme={
              isSettlementPaymentMethodArchived(item) ? 'gray' : 'successful'
            }
          />
        </Td>
        <Td>{formatDate.DDMonYYYY(String(item.updatedAt))}</Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={6} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <SettlementAccountSummary item={item} onSaved={onSaved} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default SettlementAccountsTable;
